import { useEffect, useRef, useState } from "react";

/* DeferredChart — wraps a Recharts <ResponsiveContainer> in a fixed-size box
 * and only renders its children once we have measured a positive width.
 *
 * Why: Recharts <ResponsiveContainer> logs harmless "The width(-1) and
 * height(-1) of chart should be greater than 0" warnings on first paint when
 * its parent is briefly 0px wide (tab change, modal mount, etc.). We side-step
 * this by holding the chart back one frame until the wrapping <div> reports a
 * non-zero width via ResizeObserver. Behaviour is unchanged once mounted.
 *
 * Usage:
 *   <DeferredChart height={240} testId="my-chart">
 *     <ResponsiveContainer width="100%" height="100%"> ...chart... </ResponsiveContainer>
 *   </DeferredChart>
 */
export default function DeferredChart({ height = 240, minHeight, testId, children, className = "" }) {
  const ref = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let cancelled = false;
    let raf1 = 0;
    let raf2 = 0;
    let ro = null;

    const ready = () => { if (!cancelled) setReady(true); };

    const tryMeasure = () => {
      if (cancelled) return;
      // Element is laid out — width > 0 means the browser has computed our
      // box. We can safely mount Recharts now without its width(-1) warning.
      if (el.clientWidth > 0) {
        ready();
        return;
      }
      // Not yet measured. Wait one frame and try again. If still 0 after a
      // double-frame deferral, attach a ResizeObserver as a final fallback.
      raf2 = window.requestAnimationFrame(() => {
        if (cancelled) return;
        if (el.clientWidth > 0) { ready(); return; }
        if (typeof ResizeObserver !== "undefined") {
          ro = new ResizeObserver((entries) => {
            for (const e of entries) {
              if (e.contentRect.width > 0) {
                ready();
                if (ro) { ro.disconnect(); ro = null; }
                break;
              }
            }
          });
          ro.observe(el);
        } else {
          // No ResizeObserver — last-resort timer so the chart still mounts.
          window.setTimeout(ready, 60);
        }
      });
    };

    // Wait one frame so layout has had a chance to settle, THEN measure.
    raf1 = window.requestAnimationFrame(tryMeasure);
    return () => {
      cancelled = true;
      if (raf1) window.cancelAnimationFrame(raf1);
      if (raf2) window.cancelAnimationFrame(raf2);
      if (ro) ro.disconnect();
    };
  }, []);

  const styleH = { height, minHeight: minHeight || height };
  return (
    <div
      ref={ref}
      data-testid={testId}
      className={className}
      style={styleH}
    >
      {ready ? children : null}
    </div>
  );
}
